/**
 * Mission Control's tokens.
 *
 * ⚠️ NOT the canonical design system. `src/design/tokens.ts` is — it landed
 * after this file and is what every in-app page should import from.
 *
 * This file exists because Mission Control was built in parallel with, and
 * slightly ahead of, `src/design`. The values agree in intent (same primary,
 * same semantic set, same blue-tinted elevation) but they are a second source
 * of truth, which is exactly the fragmentation `src/design` was created to
 * end.
 *
 * DO NOT add tokens here, and do not copy this pattern for a new page. The
 * open task is to migrate `components/mission/*` onto `src/design/tokens.ts`
 * and delete this file.
 *
 * Distinct from `pages/landing/landingTokens.ts` and `components/auth/
 * authTokens.ts` for a real reason, unlike this file: those serve logged-out
 * surfaces that must never follow a tenant's theme preset.
 */
export const s = {
  // ---- Brand / interaction ------------------------------------------------
  primary: '#2563eb',
  primaryDark: '#1d4ed8',
  primarySoft: '#eff4ff',
  indigo: '#6366f1',
  violet: '#8b5cf6',
  violetSoft: '#f3eeff',

  // ---- Semantic -----------------------------------------------------------
  success: '#16a34a',
  successSoft: '#e8f6ed',
  warning: '#f59e0b',
  warningSoft: '#fef4e2',
  danger: '#dc2626',
  dangerSoft: '#fdeced',
  info: '#0ea5e9',
  infoSoft: '#e6f5fd',

  // ---- Surfaces -----------------------------------------------------------
  canvas: '#f8fafc',
  paper: '#ffffff',
  raised: '#f1f5f9',
  navy: '#0b1220',

  // ---- Type ---------------------------------------------------------------
  heading: '#0f172a',
  body: '#475569',
  muted: '#94a3b8',

  // ---- Lines & elevation --------------------------------------------------
  border: '#e2e8f0',
  borderStrong: '#cbd5e1',
  /** Blue-tinted, never neutral black — black reads as dirt on a cool canvas. */
  shadowSm: '0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.05)',
  shadowMd: '0 2px 6px rgba(15,23,42,0.05), 0 8px 24px rgba(15,23,42,0.06)',
  shadowLg: '0 12px 36px rgba(15,23,42,0.11)',

  radius: 14,
  radiusSm: 10,
} as const;

/** Severity → colour pair, so an attention item can never invent a palette. */
export const severity = {
  High: [s.danger, s.dangerSoft],
  Medium: [s.warning, s.warningSoft],
  Low: [s.info, s.infoSoft],
} as const;

export type Severity = keyof typeof severity;

/** Trend direction from a signed percentage. Up is not always good — see `good`. */
export const trendTone = (delta: number, higherIsBetter = true) => {
  const up = delta >= 0;
  const good = higherIsBetter ? up : !up;
  return { up, color: good ? s.success : s.danger };
};

export const cardSx = {
  backgroundColor: s.paper,
  border: `1px solid ${s.border}`,
  borderRadius: `${s.radius}px`,
  boxShadow: s.shadowSm,
};

export const reduceMotion = {
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', transition: 'none' },
};

export const focusRing = {
  '&:focus-visible': { outline: `2px solid ${s.primary}`, outlineOffset: '2px' },
};
